import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { duration } from './primitives/tokens';
import styles from './Timeline.module.css';

const timeline = cva(styles.base);

interface TimelineItem {
  children: React.ReactNode;
  label?: React.ReactNode;
  color?: string;
  dot?: React.ReactNode;
}

interface TimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  items: TimelineItem[];
  mode?: 'left' | 'right' | 'alternate';
  pending?: React.ReactNode;
  reverse?: boolean;
}

const dotColWidth = 14; // dotSize(10) + 4

/**
 * Timeline — chronological event sequence.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | items | TimelineItem[] | — | Events |
 * | mode | 'left' \| 'right' \| 'alternate' | 'left' | Layout |
 * | pending | ReactNode | — | Pending tail item |
 * | reverse | boolean | false | Reverse order |
 *
 * @example
 * <Timeline items={[
 *   { children: 'Created thought', label: '2 hours ago' },
 *   { children: 'Edited content', color: 'var(--success)' },
 *   { children: 'Shared with team' },
 * ]} />
 */
export function Timeline({ items: rawItems, mode = 'left', pending, reverse = false, className, style, ...props }: TimelineProps) {
  let items = [...rawItems];
  if (pending) {
    items.push({ children: pending, color: 'var(--outline-variant)' });
  }
  if (reverse) items = items.reverse();

  return (
    <div className={timeline({ className })} style={style} {...props}>
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        const isRight = mode === 'right' || (mode === 'alternate' && i % 2 === 1);
        const dotColor = item.color ?? 'var(--primary)';

        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: duration.slow / 1000, delay: i * 0.05 }}
            className={clsx(
              styles.item,
              isRight && styles.itemReverse,
              !isLast && styles.itemHasMore,
            )}
          >
            {/* Label (alternate mode) */}
            {mode === 'alternate' && (
              <div className={clsx(styles.label, isRight ? styles.labelRight : styles.labelLeft)}>
                {item.label}
              </div>
            )}

            {/* Dot + line */}
            <div
              className={styles.dotCol}
              style={{ '--timeline-dot-col-width': `${dotColWidth}px` } as React.CSSProperties}
            >
              {item.dot ?? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: duration.normal / 1000, delay: i * 0.05 + 0.1 }}
                  className={styles.dot}
                  style={{ '--timeline-dot-color': dotColor } as React.CSSProperties}
                />
              )}
              {!isLast && <div className={styles.connector} />}
            </div>

            {/* Content */}
            <div
              className={clsx(
                styles.content,
                mode === 'alternate' && styles.contentAlternate,
                isRight && mode !== 'alternate' ? styles.contentRight : styles.contentLeft,
                !isLast && styles.contentPadded,
              )}
            >
              {mode !== 'alternate' && item.label && (
                <div className={styles.contentLabel}>
                  {item.label}
                </div>
              )}
              <div>
                {item.children}
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
